import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useEvent } from '../contexts/EventContext';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Car, ArrowLeft, Trophy, Medal, Award, Printer, Eye, EyeOff, Lock, Heart } from 'lucide-react';
import { toast } from 'sonner';

export default function Leaderboard() {
  const navigate = useNavigate();
  const { currentEvent, events, switchEvent } = useEvent();
  const [leaderboard, setLeaderboard] = useState([]);
  const [allLeaderboard, setAllLeaderboard] = useState([]);
  const [votingResults, setVotingResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [isPublic, setIsPublic] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [notPublicMessage, setNotPublicMessage] = useState('');
  const [showPrintView, setShowPrintView] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data && (response.data.role === 'admin' || response.data.role === 'super_admin')) {
            setIsAdmin(true);
          }
        } catch (e) {
          localStorage.removeItem('token');
          setIsAdmin(false);
        }
      }
      setAdminChecked(true);
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    if (currentEvent) {
      fetchClasses();
      fetchVotingResults();
    }
  }, [currentEvent]);

  useEffect(() => {
    if (currentEvent) {
      fetchLeaderboard();
    }
  }, [selectedClass, currentEvent]);

  const fetchClasses = async () => {
    if (!currentEvent) return;
    try {
      const response = await axios.get(`${API}/classes?event_id=${currentEvent.id}`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchVotingResults = async () => {
    if (!currentEvent) return;
    try {
      const response = await axios.get(`${API}/voting-results?event_id=${currentEvent.id}`);
      setVotingResults(response.data);
    } catch (error) {
      console.error('Error fetching voting results:', error);
    }
  };

  const fetchLeaderboard = async () => {
    if (!currentEvent) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const allUrl = `${API}/leaderboard?event_id=${currentEvent.id}`;
      const allResponse = await axios.get(allUrl, { headers });
      
      if (allResponse.data.public === false) {
        setIsPublic(false);
        setNotPublicMessage(allResponse.data.message || 'Leaderboard is not yet public');
        setLeaderboard([]);
        setAllLeaderboard([]);
      } else {
        setIsPublic(true);
        const allResults = allResponse.data.leaderboard || allResponse.data;
        setAllLeaderboard(allResults);
        
        if (selectedClass !== 'all') {
          setLeaderboard(allResults.filter(entry => entry.car.class_name === selectedClass));
        } else {
          setLeaderboard(allResults);
        }
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLeaderboardVisibility = async () => {
    try {
      const token = localStorage.getItem('token');
      const newValue = !currentEvent.leaderboard_public;
      
      await axios.put(`${API}/events/${currentEvent.id}`, {
        leaderboard_public: newValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      currentEvent.leaderboard_public = newValue;
      setIsPublic(newValue);
      
      toast.success(newValue ? 'Leaderboard is now public' : 'Leaderboard is now hidden from public');
      fetchLeaderboard();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
    }, 300);
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="h-8 w-8 text-yellow-500" />;
    if (index === 1) return <Medal className="h-8 w-8 text-gray-400" />;
    if (index === 2) return <Award className="h-8 w-8 text-amber-600" />;
    return <span className="text-2xl font-bold text-gray-500">#{index + 1}</span>;
  };

  // Normalize class names so subtle differences (em-dash vs hyphen, trailing
  // whitespace, casing, spaces-around-hyphen, zero-width characters) don't
  // cause the same logical class to render as multiple separate groups in
  // the printout. This is intentionally aggressive — only the comparison
  // key is normalized; the original string is still shown as the heading.
  const normalizeClassKey = (raw) => {
    if (!raw) return 'unclassified';
    return raw
      // Unicode-normalize so canonical/compatibility forms compare equal.
      .normalize('NFKC')
      // Remove zero-width spaces, joiners, BOMs, and other format chars.
      .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g, '')
      // Map every Unicode dash / minus / hyphen variant to ASCII '-'.
      .replace(/[\u2010-\u2015\u2212\u2043\u2E17\u2E1A\u2E3A\u2E3B\u2E40\u301C\u3030\uFE31\uFE32\uFE58\uFE63\uFF0D]/g, '-')
      // Fold curly / smart single quotes & modifier apostrophes to ASCII '.
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u02B9\u02BC\u02BB\u2035\uFF07]/g, "'")
      // Fold curly / smart double quotes to ASCII ".
      .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036\u00AB\u00BB\uFF02]/g, '"')
      // Collapse any run of whitespace (incl. NBSP, narrow NBSP, etc.) to one space.
      .replace(/[\u00A0\u202F\u205F\u3000\s]+/g, ' ')
      // Collapse spaces around hyphens: "97 - Present" == "97-Present".
      .replace(/\s*-\s*/g, '-')
      .trim()
      .toLowerCase();
  };

  const getLeaderboardByClass = () => {
    const byClass = {};
    const displayName = {};
    allLeaderboard.forEach(entry => {
      const raw = entry.car.class_name || 'Unclassified';
      const key = normalizeClassKey(raw);
      if (!byClass[key]) {
        byClass[key] = [];
        displayName[key] = (raw || 'Unclassified').trim();
      }
      byClass[key].push(entry);
    });

    Object.keys(byClass).forEach(key => {
      byClass[key].sort((a, b) => b.total_score - a.total_score);
    });

    return { byClass, displayName };
  };

  if (!currentEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  // Print View
  if (showPrintView) {
    const top5 = allLeaderboard.slice(0, 5);
    const { byClass, displayName } = getLeaderboardByClass();
    const sortedClassKeys = Object.keys(byClass).sort((a, b) =>
      (displayName[a] || '').localeCompare(displayName[b] || '')
    );

    return (
      <div className="bg-white min-h-screen p-8">
        <style>{`
          @media print {
            .no-print { display: none !important; }
            body { 
              font-size: 12pt;
              background: white !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            html, body, #root, .bg-white {
              background: white !important;
              background-color: white !important;
            }
            .page-break { page-break-before: always; }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
          @page {
            background: white;
            margin: 0.5in;
          }
        `}</style>

        {/* No-print controls */}
        <div className="no-print fixed top-4 left-1/2 transform -translate-x-1/2 z-50 flex gap-2 bg-white p-4 rounded-lg shadow-xl border border-gray-200">
          <button 
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white font-medium rounded-md"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Now
          </button>
          <button 
            type="button"
            onClick={() => setShowPrintView(false)}
            className="inline-flex items-center px-4 py-2 bg-white text-gray-900 hover:bg-gray-100 font-medium rounded-md border border-gray-300"
          >
            Back to Leaderboard
          </button>
        </div>

        {/* Print Header */}
        <div className="text-center mb-8 pt-16">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{currentEvent.name}</h1>
          <h2 className="text-xl text-gray-700">Official Results</h2>
          <p className="text-gray-600 mt-2">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        {/* Top 5 Overall (Judge Scores) */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-pink-500 pb-2">
            🏆 TOP 5 OVERALL (Judge Scores)
          </h2>
          {top5.length === 0 ? (
            <p className="text-gray-600">No scored cars yet</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-2 w-16">Rank</th>
                  <th className="text-left py-2">Vehicle</th>
                  <th className="text-left py-2">Owner</th>
                  <th className="text-left py-2">Class</th>
                  <th className="text-right py-2 w-20">Score</th>
                </tr>
              </thead>
              <tbody>
                {top5.map((entry, index) => (
                  <tr key={entry.car.id} className={`border-b border-gray-200 ${index < 3 ? 'font-semibold' : ''}`}>
                    <td className="py-2">
                      {index === 0 && '🥇 1st'}
                      {index === 1 && '🥈 2nd'}
                      {index === 2 && '🥉 3rd'}
                      {index === 3 && '4th'}
                      {index === 4 && '5th'}
                    </td>
                    <td className="py-2">{entry.car.year} {entry.car.make} {entry.car.model}</td>
                    <td className="py-2">{entry.car.owner_name}</td>
                    <td className="py-2 text-sm">{entry.car.class_name}</td>
                    <td className="py-2 text-right font-bold">{entry.total_score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* People's Choice Results */}
        {votingResults.length > 0 && (
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-pink-500 pb-2">
              ❤️ PEOPLE'S CHOICE AWARDS
            </h2>
            {votingResults.map(category => (
              <div key={category.category.id} className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 bg-pink-50 px-3 py-2 rounded mb-2">
                  {category.category.name}
                  <span className="text-sm font-normal text-gray-600 ml-2">({category.total_votes} total votes)</span>
                </h3>
                {category.results.length === 0 ? (
                  <p className="text-gray-600 text-sm ml-3">No votes yet</p>
                ) : (
                  <table className="w-full">
                    <tbody>
                      {category.results.slice(0, 5).map((result, index) => (
                        <tr key={result.car.id} className={`border-b border-gray-100 ${index === 0 ? 'font-semibold bg-yellow-50' : ''}`}>
                          <td className="py-1.5 w-12 text-center">
                            {index === 0 ? '🏆' : `${index + 1}.`}
                          </td>
                          <td className="py-1.5">
                            {result.car.year} {result.car.make} {result.car.model}
                          </td>
                          <td className="py-1.5 text-gray-700">
                            {result.car.owner_name}
                          </td>
                          <td className="py-1.5 text-right font-mono w-24">
                            {result.vote_count} {result.vote_count === 1 ? 'vote' : 'votes'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Results by Class */}
        <div className="page-break">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-pink-500 pb-2">
            📋 JUDGE RESULTS BY CLASS
          </h2>
          
          {sortedClassKeys.length === 0 ? (
            <p className="text-gray-600">No results available</p>
          ) : (
            <div className="space-y-6">
              {sortedClassKeys.map(classKey => {
                const classResults = byClass[classKey];
                const label = displayName[classKey] || classKey;

                return (
                  <div key={classKey} className="mb-4">
                    <h3 className="text-lg font-bold text-gray-900 bg-gray-100 px-3 py-2 rounded">
                      {label}
                    </h3>
                    <table className="w-full mt-2">
                      <tbody>
                        {classResults.map((entry, index) => (
                          <tr key={entry.car.id} className={`border-b border-gray-100 ${index === 0 ? 'font-semibold bg-yellow-50' : ''}`}>
                            <td className="py-1.5 w-12 text-center">
                              {index === 0 ? '🏆' : `${index + 1}.`}
                            </td>
                            <td className="py-1.5">
                              {entry.car.year} {entry.car.make} {entry.car.model}
                            </td>
                            <td className="py-1.5 text-gray-700">
                              {entry.car.owner_name}
                            </td>
                            <td className="py-1.5 text-right font-mono w-20">
                              {entry.total_score.toFixed(1)} pts
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Print Footer */}
        <div className="mt-10 pt-4 border-t border-gray-300 text-center text-gray-600 text-sm">
          <p>Printed on {new Date().toLocaleString()} • {currentEvent.name}</p>
          <p className="mt-1">Thank you for participating!</p>
        </div>
      </div>
    );
  }

  // Normal View
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button variant="ghost" className="text-gray-700 hover:text-pink-600 hover:bg-pink-50">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </Button>
            </Link>
            <Logo size="sm" showText={true} />
          </div>
          <div className="flex items-center space-x-4">
            {adminChecked && isAdmin && events && events.length > 1 && (
              <Select
                value={currentEvent?.id || ''}
                onValueChange={(eventId) => {
                  const event = events.find(e => e.id === eventId);
                  if (event) {
                    switchEvent(event);
                    setSelectedClass('all');
                  }
                }}
              >
                <SelectTrigger className="w-[200px] bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {adminChecked && isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={toggleLeaderboardVisibility}
                  className={`border-gray-300 ${currentEvent.leaderboard_public ? 'text-green-600' : 'text-orange-600'}`}
                >
                  {currentEvent.leaderboard_public ? (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Public
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Hidden
                    </>
                  )}
                </Button>
                <Button onClick={handlePrint} className="bg-pink-600 hover:bg-pink-700">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Results
                </Button>
              </>
            )}
            <Link to="/gallery">
              <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                Gallery
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {currentEvent.name} - Leaderboard
          </h1>
          {isAdmin && !currentEvent.leaderboard_public && (
            <p className="text-orange-600 flex items-center gap-2">
              <Lock className="h-4 w-4" />
              This leaderboard is currently hidden from public view
            </p>
          )}
        </div>

        {/* Class Filter */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <label className="text-gray-700 font-medium">Filter by Class:</label>
            <Select
              value={selectedClass}
              onValueChange={setSelectedClass}
            >
              <SelectTrigger className="w-[350px] bg-white border-gray-300 text-gray-900">
                <SelectValue placeholder="Select a class" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {classes.map((cls) => (
                  <SelectItem key={cls.id} value={cls.name}>{cls.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
          </div>
        ) : !isPublic && !isAdmin ? (
          <div className="text-center py-20 bg-white border border-gray-200 rounded-lg shadow-sm">
            <Lock className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <p className="text-xl text-gray-700 font-medium">{notPublicMessage}</p>
            <p className="text-gray-600 mt-2">Check back later for results!</p>
          </div>
        ) : (
          <>
            {/* Judge Scores Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Trophy className="h-6 w-6 text-yellow-500" />
                Judge Scores
              </h2>
              
              {leaderboard.length === 0 ? (
                <div className="text-center py-12 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg text-gray-700">No scored cars yet</p>
                  <p className="text-gray-600 mt-1">Results will appear here once judging begins</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {leaderboard.map((entry, index) => (
                    <div
                      key={entry.car.id}
                      className={`bg-white border rounded-lg p-6 shadow-sm ${
                        index === 0 ? 'border-2 border-yellow-400 bg-yellow-50' : ''
                      } ${index === 1 ? 'border-2 border-gray-300 bg-gray-50' : ''} ${
                        index === 2 ? 'border-2 border-amber-400 bg-amber-50' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-6">
                        <div className="flex-shrink-0 w-16 flex justify-center">
                          {getRankIcon(index)}
                        </div>

                        <div className="flex-shrink-0">
                          {entry.car.photo_data ? (
                            <img
                              src={entry.car.photo_data}
                              alt={`${entry.car.make} ${entry.car.model}`}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                              <Car className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        <div className="flex-grow">
                          <h3 className="text-2xl font-bold text-gray-900 mb-1">
                            {entry.car.year} {entry.car.make} {entry.car.model}
                          </h3>
                          <p className="text-gray-600 text-sm mb-1">
                            Registration #{entry.car.registration_number} • {entry.car.class_name}
                          </p>
                          <p className="text-sm text-gray-700">Owner: {entry.car.owner_name}</p>
                        </div>

                        <div className="flex-shrink-0 text-right">
                          <div className="text-4xl font-bold text-pink-600">
                            {entry.total_score.toFixed(1)}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {entry.num_judges} {entry.num_judges === 1 ? 'judge' : 'judges'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* People's Choice Section */}
            {votingResults.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Heart className="h-6 w-6 text-pink-500" />
                  People's Choice
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {votingResults.map(category => (
                    <div key={category.category.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                      <div className="bg-pink-50 border-b border-pink-200 px-6 py-4">
                        <h3 className="text-lg font-bold text-gray-900">{category.category.name}</h3>
                        <p className="text-sm text-gray-600">{category.total_votes} total votes</p>
                      </div>
                      
                      {category.results.length === 0 ? (
                        <div className="px-6 py-8 text-center text-gray-500">
                          No votes yet
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {category.results.slice(0, 5).map((result, index) => (
                            <div 
                              key={result.car.id} 
                              className={`px-6 py-4 flex items-center justify-between ${index === 0 ? 'bg-yellow-50' : ''}`}
                            >
                              <div className="flex items-center space-x-4">
                                <div className="w-8 text-center">
                                  {index === 0 ? (
                                    <Trophy className="h-5 w-5 text-yellow-500 mx-auto" />
                                  ) : (
                                    <span className="text-gray-500 font-medium">{index + 1}</span>
                                  )}
                                </div>
                                <div>
                                  <p className={`text-gray-900 ${index === 0 ? 'font-bold' : 'font-medium'}`}>
                                    {result.car.year} {result.car.make} {result.car.model}
                                  </p>
                                  <p className="text-sm text-gray-600">{result.car.owner_name}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className={`text-lg ${index === 0 ? 'font-bold text-pink-600' : 'font-medium text-gray-700'}`}>
                                  {result.vote_count}
                                </span>
                                <span className="text-sm text-gray-500 ml-1">
                                  {result.vote_count === 1 ? 'vote' : 'votes'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
